import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { IBudgetRequestStatus } from 'graphql/types';
import { TFilterFormValues } from 'common/types';
import { FilterBar } from 'common/components/FilterBar';
import { BoxLoader } from 'common/components/Loader';
import { Pagination } from 'common/components/Pagination';
import { usePagination } from 'common/hooks/usePagination';
import { useBudgetRequests } from 'budget/hooks/useBudgetRequests';
import { useUserBudgetRequestsFilter } from 'budget/hooks/useUserBudgetRequestsFilter';

import { UserBudgetRequestsTable } from './UserBudgetRequestsTable';

interface IProps {
    userId: string;
}

export const UserBudgetRequests = ({ userId }: IProps) => {
    const [translate] = useTranslation();

    const { paginationSettings, initializePagination, setPage } =
        usePagination();

    const {
        filters,
        filterValues,
        filterValuesForQuery,
        handleSearch,
        handleFilterChange,
    } = useUserBudgetRequestsFilter();

    // No default status: a user sees all of their own requests unless they
    // narrow it down with the status filter.
    const status = filterValuesForQuery.status as
        | IBudgetRequestStatus[]
        | undefined;

    const search = filterValues.q;

    const { budgetRequests, count, loading } = useBudgetRequests({
        userIds: [userId],
        offset: paginationSettings.offset,
        first: paginationSettings.first,
        status,
        q: search,
    });

    useEffect(() => {
        initializePagination(count);
    }, [count, initializePagination]);

    const onSearch = (searchValue: string) => {
        setPage(1);
        handleSearch(searchValue);
    };

    const onFiltersChange = (values?: TFilterFormValues) => {
        setPage(1);
        handleFilterChange(values);
    };

    const noResults = !loading && !budgetRequests.length;

    return (
        <Box>
            <FilterBar
                useFilterDrawer
                disabled={loading}
                filterDrawerProps={{
                    title: translate('userBudgetRequests.filterDrawer.title'),
                }}
                formFilters={filters}
                formFilterValues={filterValues}
                initialSearchValue={search}
                placeholder={translate('userBudgetRequests.searchPlaceholder')}
                onFiltersChange={onFiltersChange}
                onSearch={onSearch}
                onSearchClear={() => onSearch('')}
            />

            {loading && !budgetRequests.length && <BoxLoader />}

            {noResults && (
                <Typography sx={{ mt: 4, textAlign: 'center' }}>
                    {translate('userBudgetRequests.noResults')}
                </Typography>
            )}

            {!!budgetRequests.length && (
                <>
                    <UserBudgetRequestsTable budgetRequests={budgetRequests} />

                    <Box sx={{ mt: 4 }}>
                        <Pagination
                            page={paginationSettings.page}
                            pageAmount={paginationSettings.pageAmount}
                            totalsAmount={paginationSettings.count}
                            totalsText={translate('userBudgetRequests.totals', {
                                count: paginationSettings.count,
                            })}
                            onChange={setPage}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};
